import React, { useRef, useState } from 'react';
import { Box, Button, Popover, PopoverProps, Stack, Typography } from '@mui/material';

import { Point2D as Point2DInterface } from '@paragon/contract-library';
import { DistanceField } from '@paragon/material';

import { CardinalDirection, keyboardCodeToDirection, PointMovement } from '../../library/layout-geometry/directions';
import { DirectionButtons } from './DirectionButtons';

export type PointMovementPopoverAnchor =
  | { readonly element: HTMLElement }
  | {
      /** screen position where the mouse was clicked */
      readonly clientPosition: Point2DInterface;
    };

const OFFSET_FROM_PICKED_POINTS = 8;

function getAnchorProps(anchor: PointMovementPopoverAnchor): Partial<PopoverProps> {
  return 'element' in anchor
    ? {
        anchorEl: anchor.element,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        transformOrigin: { vertical: 'top', horizontal: 'center' },
      }
    : {
        anchorReference: 'anchorPosition',
        anchorPosition: {
          top: anchor.clientPosition.y + OFFSET_FROM_PICKED_POINTS,
          left: anchor.clientPosition.x + OFFSET_FROM_PICKED_POINTS,
        },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
      };
}

export function PointMovementPopover(props: {
  anchor: PointMovementPopoverAnchor;
  onMove(movement: PointMovement): void;
  onClose(): void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [distance, setDistance] = useState<number | null>(null);
  const [direction, setDirection] = useState<CardinalDirection | null>(null);

  function move(distanceToMove = distance) {
    if (direction == null) return;

    if (distanceToMove == null) {
      inputRef.current?.focus();
      return;
    }

    props.onMove({ direction, distance: distanceToMove });
  }

  return (
    <Popover
      open
      disableRestoreFocus
      className="clickable-during-picking"
      {...getAnchorProps(props.anchor)}
      onClose={() => props.onClose()}
      onKeyDown={(event) => {
        event.stopPropagation();

        if (event.key === 'Enter' && !(event.target instanceof HTMLButtonElement)) {
          event.preventDefault();
          move();
        }
      }}
    >
      <Stack spacing={2} sx={{ padding: 2, width: 400 }}>
        <Typography variant="h5">Move Point</Typography>

        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <DistanceField
            autoFocus
            inputRef={inputRef}
            label="Distance"
            margin="none"
            sx={{ flexGrow: 1, minWidth: 0 }}
            canBeNull
            canSubmitWhenNull
            blurOnEnter={false}
            value={distance}
            onSubmit={(submittedDistance, { finalizing }) => {
              setDistance(submittedDistance);
              if (finalizing) move(submittedDistance);
            }}
            onKeyDown={(event) => {
              const keyDirection = keyboardCodeToDirection(event.code);
              if (keyDirection == null) return;

              event.preventDefault();
              setDirection(keyDirection);
            }}
          />

          <Box sx={{ flexShrink: 0 }}>
            <DirectionButtons value={direction} onChange={(_event, value) => setDirection(value)} />
          </Box>
        </Stack>

        <Typography variant="body2" color="textSecondary">
          Enter a distance and choose a direction (or use the arrow keys), then press Enter or Move.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
          <Button onClick={() => props.onClose()}>Cancel</Button>

          <Button variant="contained" disabled={direction == null} onClick={() => move()}>
            Move
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
}
